import React from "react";
import styled from "styled-components";

const SIZE_MAP = {
  large: { ring: 168, stroke: 3.5, hub: 40, font: 16, gap: 28 },
  medium: { ring: 132, stroke: 3, hub: 32, font: 15, gap: 24 },
  small: { ring: 80, stroke: 2.5, hub: 20, font: 12, gap: 16 },
};

export default function Loader({
  size = "medium",
  theme = "default",
  label = "Loading",
}) {
  const dims = SIZE_MAP[size] || SIZE_MAP.medium;

  return (
    <Wrapper $dims={dims}>
      <div className="de-loader" role="status" aria-live="polite" aria-label={label}>
        <div className="de-loader-aura" aria-hidden="true" />
        <div className="de-loader-core">
          <span className="de-ring de-ring-glow" />
          <span className="de-ring de-ring-outer" />
          <span className="de-ring de-ring-mid" />
          <span className="de-ring de-ring-inner" />
          <span className="de-dash-ring" aria-hidden="true" />
          <span className="de-orbit de-orbit-a">
            <span className="de-orbit-dot" />
          </span>
          <span className="de-orbit de-orbit-b">
            <span className="de-orbit-dot de-orbit-dot-b" />
          </span>
          <span className="de-hub">
            <span className="de-hub-ring" />
            <span className="de-hub-pulse" />
            <span className="de-hub-core" />
          </span>
        </div>
        <p className="de-loader-label">
          <span className="de-loader-text">{label}</span>
          <span className="de-loader-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        </p>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  --de-navy: #032b4e;
  --de-navy-mid: #0a4a78;
  --de-navy-soft: rgba(3, 43, 78, 0.2);
  --de-accent: #e06639;
  --de-accent-soft: rgba(224, 102, 57, 0.45);
  --de-ring: ${(p) => p.$dims.ring}px;
  --de-stroke: ${(p) => p.$dims.stroke}px;
  --de-hub: ${(p) => p.$dims.hub}px;
  --de-font: ${(p) => p.$dims.font}px;
  --de-gap: ${(p) => p.$dims.gap}px;

  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 20px 0 28px;

  .de-loader {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--de-gap);
  }

  .de-loader-aura {
    position: absolute;
    top: calc(var(--de-ring) / 2 - 8px);
    left: 50%;
    width: calc(var(--de-ring) * 1.35);
    height: calc(var(--de-ring) * 1.35);
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(224, 102, 57, 0.16) 0%,
      rgba(3, 43, 78, 0.1) 42%,
      transparent 72%
    );
    animation: de-aura 2.4s ease-in-out infinite;
    pointer-events: none;
  }

  .de-loader-core {
    position: relative;
    width: var(--de-ring);
    height: var(--de-ring);
    z-index: 1;
  }

  .de-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: var(--de-stroke) solid transparent;
    box-sizing: border-box;
  }

  .de-ring-glow {
    inset: -6%;
    border-width: 1px;
    border-color: rgba(224, 102, 57, 0.18);
    animation: de-pulse-ring 2s ease-in-out infinite;
  }

  .de-ring-outer {
    border-top-color: var(--de-navy);
    border-right-color: var(--de-navy-soft);
    border-bottom-color: transparent;
    border-left-color: rgba(10, 74, 120, 0.45);
    filter: drop-shadow(0 0 6px rgba(3, 43, 78, 0.35));
    animation: de-spin 1.15s linear infinite;
  }

  .de-ring-mid {
    inset: 11%;
    border-bottom-color: var(--de-accent);
    border-left-color: var(--de-accent-soft);
    border-top-color: transparent;
    border-right-color: rgba(224, 102, 57, 0.2);
    filter: drop-shadow(0 0 8px rgba(224, 102, 57, 0.4));
    animation: de-spin-rev 0.9s linear infinite;
  }

  .de-ring-inner {
    inset: 24%;
    border-top-color: var(--de-navy-mid);
    border-right-color: transparent;
    border-bottom-color: transparent;
    border-left-color: var(--de-navy-soft);
    animation: de-spin 0.65s linear infinite;
  }

  .de-dash-ring {
    position: absolute;
    inset: 6%;
    border-radius: 50%;
    border: 1.5px dashed rgba(3, 43, 78, 0.28);
    box-sizing: border-box;
    animation: de-spin-rev 4s linear infinite;
  }

  .de-orbit {
    position: absolute;
    inset: 0;
  }

  .de-orbit-a {
    animation: de-spin 1.8s linear infinite;
  }

  .de-orbit-b {
    animation: de-spin-rev 2.4s linear infinite;
  }

  .de-orbit-dot {
    position: absolute;
    top: 0;
    left: 50%;
    width: calc(var(--de-hub) * 0.34);
    height: calc(var(--de-hub) * 0.34);
    margin-left: calc(var(--de-hub) * -0.17);
    border-radius: 50%;
    background: var(--de-accent);
    box-shadow:
      0 0 10px var(--de-accent-soft),
      0 0 20px rgba(224, 102, 57, 0.35);
  }

  .de-orbit-dot-b {
    top: auto;
    bottom: 8%;
    width: calc(var(--de-hub) * 0.24);
    height: calc(var(--de-hub) * 0.24);
    margin-left: calc(var(--de-hub) * -0.12);
    background: var(--de-navy-mid);
    box-shadow: 0 0 8px rgba(10, 74, 120, 0.5);
  }

  .de-hub {
    position: absolute;
    top: 50%;
    left: 50%;
    width: var(--de-hub);
    height: var(--de-hub);
    margin: calc(var(--de-hub) / -2) 0 0 calc(var(--de-hub) / -2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .de-hub-ring {
    position: absolute;
    inset: -18%;
    border-radius: 50%;
    border: 2px solid rgba(224, 102, 57, 0.35);
    animation: de-pulse-ring 1.4s ease-in-out infinite;
  }

  .de-hub-core {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: linear-gradient(145deg, #021a30 0%, #032b4e 50%, #0a4a78 100%);
    box-shadow:
      0 0 0 2px rgba(255, 255, 255, 0.1),
      0 0 22px rgba(3, 43, 78, 0.45),
      inset 0 0 12px rgba(224, 102, 57, 0.15);
    animation: de-hub-breathe 1.8s ease-in-out infinite;
  }

  .de-hub-pulse {
    position: absolute;
    width: 42%;
    height: 42%;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #ff9a6b 0%, #e06639 55%, #c4542e 100%);
    box-shadow: 0 0 14px rgba(224, 102, 57, 0.7);
    animation: de-pulse 1s ease-in-out infinite;
    z-index: 1;
  }

  .de-loader-label {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-family: Poppins, sans-serif;
    font-size: var(--de-font);
    font-weight: 800;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--de-navy);
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.4);
    animation: de-label-glow 2s ease-in-out infinite;
  }

  .de-loader-sub {
    margin: -10px 0 0;
    font-family: Poppins, sans-serif;
    font-size: calc(var(--de-font) * 0.72);
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #6a7d8c;
    animation: de-fade-slide 2.2s ease-in-out infinite;
  }

  .de-loader-dots {
    display: inline-flex;
    gap: 4px;
    align-items: flex-end;
    height: 1em;
  }

  .de-loader-dots i {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--de-accent);
    display: block;
    box-shadow: 0 0 6px var(--de-accent-soft);
    animation: de-dot 0.85s ease-in-out infinite;
  }

  .de-loader-dots i:nth-child(2) {
    animation-delay: 0.12s;
  }

  .de-loader-dots i:nth-child(3) {
    animation-delay: 0.24s;
  }

  @keyframes de-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes de-spin-rev {
    to {
      transform: rotate(-360deg);
    }
  }

  @keyframes de-pulse {
    0%,
    100% {
      transform: scale(0.7);
      opacity: 0.7;
    }
    50% {
      transform: scale(1.25);
      opacity: 1;
    }
  }

  @keyframes de-pulse-ring {
    0%,
    100% {
      transform: scale(0.96);
      opacity: 0.45;
    }
    50% {
      transform: scale(1.06);
      opacity: 1;
    }
  }

  @keyframes de-hub-breathe {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.06);
    }
  }

  @keyframes de-aura {
    0%,
    100% {
      opacity: 0.55;
      transform: translate(-50%, -50%) scale(0.92);
    }
    50% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1.08);
    }
  }

  @keyframes de-dot {
    0%,
    80%,
    100% {
      opacity: 0.3;
      transform: translateY(0) scale(0.85);
    }
    40% {
      opacity: 1;
      transform: translateY(-5px) scale(1.15);
    }
  }

  @keyframes de-label-glow {
    0%,
    100% {
      opacity: 0.85;
    }
    50% {
      opacity: 1;
    }
  }

  @keyframes de-fade-slide {
    0%,
    100% {
      opacity: 0.45;
      letter-spacing: 0.14em;
    }
    50% {
      opacity: 0.95;
      letter-spacing: 0.2em;
    }
  }
`;
